// MovieList.js - Academic Style
import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Button,
  Container,
  Pagination,
  TextField,
  CircularProgress,
  useMediaQuery,
  Chip,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  School,
  Search,
  VideoLibrary,
  PlayCircle,
  AccountCircle,
  ExitToApp,
  MenuBook,
  Category,
  AdminPanelSettings,
} from "@mui/icons-material";
import { getAllMovies } from "../api/moviesApi";
import { useAuth } from "../contexts/AuthContext";

const LanguageFilterButtons = ({
  languageFilter,
  setLanguageFilter,
  setPage,
}) => {
  const buttons = [
    { label: "ทั้งหมด", value: "all", color: "#1565C0", icon: <Category /> },
    { label: "คอร์สเรียน", value: "dub", color: "#2E7D32", icon: <MenuBook /> },
    {
      label: "วิดีโอสำคัญ",
      value: "sub",
      color: "#D32F2F",
      icon: <VideoLibrary />,
    },
  ];

  return (
    <Box
      sx={{
        textAlign: "center",
        mb: 4,
        display: "flex",
        justifyContent: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      {buttons.map(({ label, value, color, icon }) => {
        const selected = languageFilter === value;
        return (
          <Button
            key={value}
            onClick={() => {
              setLanguageFilter(value);
              setPage(1);
            }}
            variant={selected ? "contained" : "outlined"}
            startIcon={icon}
            sx={{
              minWidth: 120,
              borderRadius: 3,
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: selected ? color : "transparent",
              borderColor: color,
              color: selected ? "white" : color,
              boxShadow: selected ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: selected ? color : `${color}15`,
                borderColor: color,
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
              },
            }}
          >
            {label}
          </Button>
        );
      })}
    </Box>
  );
};

const MovieList = () => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [languageFilter, setLanguageFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 12;
  const { logout, user } = useAuth();

  // ตรวจสอบสิทธิ์ admin
  const username = user?.username || localStorage.getItem("username") || "";
  const isAdmin = username === "smoker8888";

  const fetchMovies = async () => {
    try {
      setLoading(true);
      const movieData = await getAllMovies();
      setMovies(movieData);
    } catch (error) {
      console.error("Error fetching movies:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovies();
  }, []);

  const theme = createTheme({
    palette: {
      mode: "light",
      background: {
        default: "#F8F9FA",
        paper: "#FFFFFF",
      },
      primary: {
        main: "#1565C0",
      },
      secondary: {
        main: "#FF8F00",
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontWeight: 700,
        color: "#1565C0",
      },
      h6: {
        fontWeight: 600,
      },
    },
  });

  const filteredMovies = movies
    .filter((movie) => {
      if (languageFilter === "all") return true;
      return movie.languageType === languageFilter;
    })
    .filter((movie) =>
      movie.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const indexOfLastMovie = page * itemsPerPage;
  const indexOfFirstMovie = indexOfLastMovie - itemsPerPage;
  const currentMovies = filteredMovies.slice(
    indexOfFirstMovie,
    indexOfLastMovie
  );
  const pageCount = Math.ceil(filteredMovies.length / itemsPerPage);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <AppBar
          position="sticky"
          sx={{
            backgroundColor: "#FFFFFF",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            backdropFilter: "blur(10px)",
            color: "#1565C0",
          }}
        >
          <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexGrow: 1,
                cursor: "pointer",
                minWidth: 0,
                overflow: "hidden",
              }}
              onClick={() => navigate("/dashboard")}
            >
              {!isMobile && (
                <School sx={{ mr: 2, color: "#1565C0", flexShrink: 0 }} />
              )}
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  sx={{
                    fontWeight: 700,
                    color: "#1565C0",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  ห้องสมุดดิจิทัล
                </Typography>
                {!isMobile && (
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Digital Learning Center
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{ display: "flex", gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}
            >
              {/* ปุ่มโปรไฟล์ */}
              <Button
                color="inherit"
                startIcon={!isMobile ? <AccountCircle /> : undefined}
                onClick={() => navigate("/profile")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                  px: { xs: 1, sm: 2 },
                  color: "#1565C0",
                  "&:hover": {
                    backgroundColor: "#E3F2FD",
                  },
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {isMobile ? <AccountCircle /> : "โปรไฟล์"}
              </Button>

              {/* ปุ่มแอดมิน - เฉพาะ smoker8888 */}
              {isAdmin && (
                <Button
                  color="inherit"
                  startIcon={!isMobile ? <AdminPanelSettings /> : undefined}
                  onClick={() => navigate("/admin")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: "auto",
                    px: { xs: 1, sm: 2 },
                    color: "#FF8F00",
                    "&:hover": {
                      backgroundColor: "#FFF3E0",
                    },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  {isMobile ? <AdminPanelSettings /> : "แอดมิน"}
                </Button>
              )}

              {/* ปุ่มออกจากระบบ */}
              <Button
                color="inherit"
                startIcon={!isMobile ? <ExitToApp /> : undefined}
                onClick={() => {
                  logout?.();
                  localStorage.clear();
                  navigate("/academic");
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                  px: { xs: 1, sm: 2 },
                  justifyContent: "center",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {isMobile ? <ExitToApp /> : "ออกจากระบบ"}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        <Container
          sx={{
            maxWidth: 1440,
            mx: "auto",
            py: 4,
            px: 2,
            minHeight: "100vh",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{ mb: 1, color: "#1565C0" }}
            >
              📚 คลังความรู้ดิจิทัล
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: "#666" }}>
              Digital Knowledge Repository
            </Typography>
            <Paper
              sx={{
                maxWidth: 500,
                mx: "auto",
                p: 2,
                display: "flex",
                alignItems: "center",
                backgroundColor: "#F8F9FA",
                border: "2px solid #E3F2FD",
                borderRadius: 3,
              }}
            >
              <Search sx={{ mr: 2, color: "#1565C0" }} />
              <TextField
                placeholder="ค้นหาเนื้อหาการเรียนรู้..."
                variant="standard"
                fullWidth
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setLanguageFilter("all");
                  setPage(1);
                }}
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "1.1rem",
                  },
                }}
              />
            </Paper>
          </Box>

          {/* ปุ่มฟิลเตอร์ภาษา */}
          <LanguageFilterButtons
            languageFilter={languageFilter}
            setLanguageFilter={setLanguageFilter}
            setPage={setPage}
          />

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3} justifyContent="center">
              {currentMovies.map((movie) => (
                <Grid item xs={6} sm={6} md={4} lg={3} key={movie._id}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      transition: "all 0.3s ease",
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E3F2FD",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 8px 24px rgba(21,101,192,0.15)",
                      },
                    }}
                  >
                    <CardActionArea
                      sx={{ height: "100%" }}
                      onClick={() =>
                        navigate(`/learn/${movie._id}`, { state: { movie } })
                      }
                    >
                      <Box sx={{ position: "relative", height: "100%" }}>
                        {/* 🏷️ Category Badge */}
                        {movie.languageType && (
                          <Chip
                            label={
                              movie.languageType === "dub"
                                ? "คอร์สเรียน"
                                : movie.languageType === "sub"
                                ? "วิดีโอสำคัญ"
                                : "ทั่วไป"
                            }
                            sx={{
                              position: "absolute",
                              top: 12,
                              left: 12,
                              backgroundColor:
                                movie.languageType === "dub"
                                  ? "#2E7D32"
                                  : movie.languageType === "sub"
                                  ? "#D32F2F"
                                  : "#757575",
                              color: "white",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              zIndex: 2,
                            }}
                          />
                        )}

                        {/* 🎯 Play Icon Overlay */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 1,
                            opacity: 0,
                            transition: "opacity 0.3s ease",
                            ".MuiCard-root:hover &": {
                              opacity: 1,
                            },
                          }}
                        >
                          <PlayCircle
                            sx={{
                              fontSize: 60,
                              color: "rgba(255,255,255,0.9)",
                              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))",
                            }}
                          />
                        </Box>

                        {/* � Course Thumbnail */}
                        <CardMedia
                          component="img"
                          height="200"
                          image={movie.poster || "/course-placeholder.svg"}
                          alt={movie.title}
                          onError={(e) => {
                            e.target.src = "/course-placeholder.svg";
                          }}
                          sx={{
                            objectFit: "cover",
                            backgroundColor: "#F5F5F5",
                          }}
                        />

                        {/* � Course Info */}
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ position: "relative" }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                color: "#1565C0",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                lineHeight: 1.3,
                                cursor:
                                  movie.title.length > 40
                                    ? "pointer"
                                    : "default",
                              }}
                              title={
                                movie.title.length > 40
                                  ? movie.title
                                  : undefined
                              }
                            >
                              {movie.title}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mt: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#666",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              � {movie.viewCount || 0} ผู้เรียน
                            </Typography>
                            <Chip
                              label="เปิดเรียน"
                              size="small"
                              sx={{
                                backgroundColor: "#E8F5E8",
                                color: "#2E7D32",
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Box
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              backgroundColor: "#FFFFFF",
              backdropFilter: "blur(6px)",
              px: 2,
              py: 1,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              border: "1px solid #E3F2FD",
              zIndex: 9999,
            }}
          >
            <Pagination
              count={pageCount}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              siblingCount={1}
              boundaryCount={1}
              sx={{
                "& .MuiPaginationItem-root": {
                  fontWeight: "bold",
                  color: "#1565C0",
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "#1565C0",
                  color: "white",
                },
              }}
            />
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default MovieList;
